/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Box,
  Divider,
  Flex,
  Link,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Scrollbar,
  Space,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useToggle } from '@tonic-one/react-hooks';
import CSpinner from '../../../components/CSpinner';
import { useEffect, useRef, useState } from 'react';
import { Steps } from 'antd';
import {
  DataSyncFailed,
  DataSynced,
  DataSyncing,
  EnablePolicyRadioGroup,
  GmailNote,
  GoogleLoginSvg,
  ProvisionOrgInfo,
  StepIcon,
  UserIcon,
} from './share';
import { useTranslation } from 'react-i18next';
import { deleteCookie, getProvisionCookie } from '../../../utils/common';
import { ProvisionApi } from '@Apis';
import {
  AuthenticationInfoRes,
  EnabledPolicyTypeEnum,
  IdSyncStatus,
  ProvisionModalState,
  ProvisionStatusEnum,
  TokenStatus,
} from '../../../types/provision';
import ErrorAlert from '../../../components/Common/ErrorAlert';
import { reportPendo } from '../../../utils/setupPendo';
import { Service } from '@Types/app';

enum GoogleDriveStatus {
  NotConfirmed = 0,
  NotProvision,
  Provisioning,
  Provisioned,
}

const SERVICE = Service.GoogleDrive;

const GoogleDriveModal = ({
  orgId,
  onClose,
  isOpen,
  provisionStatus,
}: ProvisionModalState) => {
  const { t } = useTranslation(['provision', 'common']);
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const StatusMap = {
    [ProvisionStatusEnum.NotStarted]: GoogleDriveStatus.NotConfirmed,
    [ProvisionStatusEnum.Provisioning]: GoogleDriveStatus.Provisioning,
    [ProvisionStatusEnum.Provisioned]: GoogleDriveStatus.Provisioned,
  };

  const [syncDataFailFlag, setSyncDataFailFlag] = useState(
    provisionStatus.status === ProvisionStatusEnum.Failure &&
      [IdSyncStatus.Failure, 'FAILURE'].includes(
        provisionStatus?.idSync?.status
      )
  );
  const [googleDriveStatus, setGoogleDriveStatus] = useState<GoogleDriveStatus>(
    () => {
      if (Object.keys(StatusMap).includes(provisionStatus.status)) {
        return StatusMap[provisionStatus.status as keyof typeof StatusMap];
      } else {
        //provisionStatus.status = failure
        //token failure
        if (provisionStatus.authorization.status === TokenStatus.Failure) {
          return GoogleDriveStatus.NotConfirmed;
        } else {
          //id sync fail
          return GoogleDriveStatus.Provisioning;
        }
      }
    }
  );
  const [authenticationInfo, setAuthenticationInfo] =
    useState<null | AuthenticationInfoRes>(null);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (googleDriveStatus === GoogleDriveStatus.NotConfirmed) {
      ProvisionApi.apiGetAuthenticationInfo(SERVICE, orgId).then((res) => {
        setAuthenticationInfo(res.data);
      });
    }
  }, []);

  return (
    <Modal isOpen={isOpen} isClosable onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('provision.service.googleDrive.title')}</ModalHeader>
        <Scrollbar overflow="scroll" maxHeight="60vh">
          <ModalBody mt="0px !important">
            <ProvisionOrgInfo orgId={orgId} />

            {errorMsg && (
              <ErrorAlert
                errorMsg={errorMsg}
                onClose={() => setErrorMsg(undefined)}
              />
            )}
            <Flex direction="column" gap="16px">
              <Steps
                direction="vertical"
                current={googleDriveStatus}
                items={[
                  {
                    title: (
                      <Text>
                        {t('provision.service.googleDrive.installApp')}{' '}
                        <Link
                          display="inline"
                          onClick={() => {
                            window.open(
                              authenticationInfo?.[0]?.installAppUrl || '',
                              '_blank'
                            );
                          }}
                        >
                          {t('common.action.label.clickHere')}
                        </Link>
                      </Text>
                    ),
                    description:
                      googleDriveStatus === GoogleDriveStatus.NotConfirmed ? (
                        <Button
                          variant="secondary"
                          borderRadius="2rem"
                          onClick={() =>
                            setGoogleDriveStatus(GoogleDriveStatus.NotProvision)
                          }
                        >
                          {t('provision.common.installConfirm')}
                        </Button>
                      ) : (
                        <Text color={colorStyle.color.tertiary} mt="2x">
                          {t('provision.common.installConfirmed')}
                        </Text>
                      ),
                    icon: <UserIcon />,
                  },
                  {
                    title: (
                      <Text color={colorStyle.color.primary}>
                        {t('provision.service.googleDrive.grantPermission')}
                      </Text>
                    ),
                    description:
                      googleDriveStatus <= GoogleDriveStatus.NotProvision ? (
                        <GrantPermissionAndEnablePolicy
                          googleDriveStatus={googleDriveStatus}
                          setGoogleDriveStatus={setGoogleDriveStatus}
                          authenticationInfo={authenticationInfo}
                          setErrorMsg={setErrorMsg}
                          orgId={orgId}
                        />
                      ) : (
                        <Text color={colorStyle.color.tertiary}>
                          {t('provision.common.permissionGranted')}
                        </Text>
                      ),
                    icon: (
                      <StepIcon
                        checked={
                          googleDriveStatus >= GoogleDriveStatus.Provisioning
                        }
                      />
                    ),
                  },
                ]}
              />
              {googleDriveStatus === GoogleDriveStatus.Provisioning &&
                (syncDataFailFlag ? (
                  <DataSyncFailed
                    service={SERVICE}
                    onRetry={async () => {
                      await ProvisionApi.apiRetrySyncData(SERVICE, orgId);
                      setGoogleDriveStatus(GoogleDriveStatus.Provisioning);
                      setSyncDataFailFlag(false);
                    }}
                  />
                ) : (
                  <DataSyncing
                    onRefresh={async () => {
                      const res = await ProvisionApi.apiGetProvisionStatus(
                        SERVICE,
                        orgId
                      );
                      if (res.data.status === ProvisionStatusEnum.Provisioned) {
                        setGoogleDriveStatus(GoogleDriveStatus.Provisioned);
                      }
                    }}
                  />
                ))}
              {googleDriveStatus === GoogleDriveStatus.Provisioned && (
                <DataSynced />
              )}
              <GmailNote />
            </Flex>
          </ModalBody>
        </Scrollbar>
        <ModalFooter>
          <Button minWidth="20x" onClick={onClose}>
            {t('common.action.label.close')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default GoogleDriveModal;

const GrantPermissionAndEnablePolicy = ({
  googleDriveStatus,
  authenticationInfo,
  setGoogleDriveStatus,
  setErrorMsg,
  orgId,
}: {
  googleDriveStatus: GoogleDriveStatus;
  authenticationInfo: AuthenticationInfoRes | null;
  setGoogleDriveStatus: (status: GoogleDriveStatus) => void;
  setErrorMsg: (msg: string | undefined) => void;
  orgId: string;
}) => {
  const { t } = useTranslation('provision');
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [loading, toggleLoading] = useToggle(false);
  const radioGroupRef = useRef<{
    enabledPolicyType: EnabledPolicyTypeEnum;
  }>({
    enabledPolicyType: EnabledPolicyTypeEnum.Default,
  });
  const timeRef = useRef<any>(null);
  const onClick = async () => {
    const authUrl = authenticationInfo?.[0]?.authenticationUrl;
    if (!authUrl) {
      return;
    }
    reportPendo('Provision.GrantPermission.Click', {
      orgId,
      service: SERVICE,
    });
    setErrorMsg(undefined);
    toggleLoading(true);
    window.open(authUrl, '_blank');
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    timeRef.current = setInterval(async () => {
      const googleDriveProvisionInfo = getProvisionCookie(
        'provision_status_googledrive'
      );
      if (googleDriveProvisionInfo) {
        clearInterval(timeRef.current);
        deleteCookie('provision_status_');
        const { error, code, state } = JSON.parse(googleDriveProvisionInfo);
        if (error) {
          setErrorMsg(t('provision.service.googleDrive.error.deniedGrant'));
        } else {
          try {
            const res = await ProvisionApi.apiSaveProvisionInfoForStorage(
              SERVICE,
              {
                authCode: code,
                csrfCode: state,
              },
              radioGroupRef.current.enabledPolicyType,
              orgId
            );
            if (res.data?.status === 'failure') {
              // failure data = {
              //     "status": "failure",
              //     "reason": "FAILURE;451"
              // }
              setErrorMsg(
                t([
                  `provision.service.googleDrive.error.saveInfo.${res.data?.reason}`,
                  'common.apiException.internalError',
                ])
              );
            } else {
              setGoogleDriveStatus(GoogleDriveStatus.Provisioning);
            }
          } catch (e) {
            console.error(JSON.stringify(e));
          }
        }
        toggleLoading(false);
      }
    }, 1000);
  };
  useEffect(() => {
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    return () => {
      if (timeRef.current) {
        clearInterval(timeRef.current);
      }
    };
  }, []);

  return (
    <>
      {loading && <CSpinner />}
      <Flex direction="column" gap="16px" color={colorStyle.color.primary}>
        <Space />
        <Divider variant="dashed" orientation="horizontal" />
        <Text color={colorStyle.color.secondary}>
          {/* TODO: i18n "Select the policy to enable when the access grant is completed:" */}
          {t('provision.common.selectPolicyToEnable')}
        </Text>
        <EnablePolicyRadioGroup
          service={Service.GoogleDrive}
          ref={radioGroupRef}
        />
        <Divider variant="dashed" orientation="horizontal" />
        <Box>
          <Button
            px="0px"
            borderWidth="0px"
            variant="ghost"
            disabled={!(googleDriveStatus === GoogleDriveStatus.NotProvision)}
            onClick={onClick}
          >
            <GoogleLoginSvg disabled={false} />
          </Button>
        </Box>
      </Flex>
    </>
  );
};
